import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Button,
    Empty,
    List,
    Popconfirm,
    Space,
    Typography,
    Upload,
    message,
} from 'antd';
import {
    DeleteOutlined,
    DownloadOutlined,
    FileOutlined,
    InboxOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { filesApi, productPackagingApi } from '../../../api/client';
import type { PackagingFile } from '../../bdo/types';

const { Text } = Typography;

const ALLOWED_MIME = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
];
const ALLOWED_EXT = ['pdf', 'jpg', 'jpeg', 'png', 'xlsx', 'xls', 'docx', 'doc'];

export default function PackagingFiles({ productId }: { productId: string }) {
    const queryClient = useQueryClient();

    const { data: files = [], isLoading } = useQuery<PackagingFile[]>({
        queryKey: ['packaging-files', productId],
        queryFn: () =>
            productPackagingApi.getFiles(productId).then((res: any) => res.data),
        enabled: !!productId,
    });

    const uploadMutation = useMutation({
        mutationFn: (file: File) => productPackagingApi.uploadFile(productId, file),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['packaging-files', productId] });
            queryClient.invalidateQueries({ queryKey: ['product-packaging', productId] });
            message.success('Plik wgrany');
        },
        onError: (error: any) =>
            message.error(error?.response?.data?.message || 'Błąd wgrywania pliku'),
    });

    const deleteMutation = useMutation({
        mutationFn: (fileId: string) =>
            productPackagingApi.deleteFile(productId, fileId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['packaging-files', productId] });
            queryClient.invalidateQueries({ queryKey: ['product-packaging', productId] });
            message.success('Plik usunięty');
        },
        onError: () => message.error('Nie udało się usunąć pliku'),
    });

    const formatSize = (size: number | string) => {
        const bytes = Number(size) || 0;
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    return (
        <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Text strong>Załączniki</Text>
            <Upload.Dragger
                multiple
                showUploadList={false}
                accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.docx,.doc"
                customRequest={({ file, onSuccess, onError }) => {
                    uploadMutation.mutate(file as File, {
                        onSuccess: () => onSuccess?.({}),
                        onError: (err) => onError?.(err as Error),
                    });
                }}
                beforeUpload={(file) => {
                    const ext = (file.name.split('.').pop() || '').toLowerCase();
                    if (!ALLOWED_EXT.includes(ext) || !ALLOWED_MIME.includes(file.type)) {
                        message.error(
                            'Dozwolone typy: PDF, JPG, JPEG, PNG, XLSX, XLS, DOCX, DOC',
                        );
                        return Upload.LIST_IGNORE;
                    }
                    return true;
                }}
            >
                <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                </p>
                <p className="ant-upload-text">Kliknij lub przeciągnij plik</p>
                <p className="ant-upload-hint" style={{ fontSize: 12 }}>
                    PDF, JPG, PNG, XLSX, XLS, DOCX, DOC
                </p>
            </Upload.Dragger>

            {isLoading ? (
                <Text type="secondary">Ładowanie plików...</Text>
            ) : files.length === 0 ? (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Brak załączników"
                />
            ) : (
                <List
                    size="small"
                    dataSource={files}
                    renderItem={(file) => (
                        <List.Item
                            actions={[
                                <Button
                                    key="download"
                                    type="link"
                                    size="small"
                                    icon={<DownloadOutlined />}
                                    href={filesApi.getDownloadUrl(file.id)}
                                    target="_blank"
                                >
                                    Pobierz
                                </Button>,
                                <Popconfirm
                                    key="delete"
                                    title="Usunąć plik?"
                                    okText="Usuń"
                                    cancelText="Anuluj"
                                    okButtonProps={{ danger: true }}
                                    onConfirm={() => deleteMutation.mutate(file.id)}
                                >
                                    <Button
                                        type="text"
                                        danger
                                        size="small"
                                        icon={<DeleteOutlined />}
                                    />
                                </Popconfirm>,
                            ]}
                        >
                            <List.Item.Meta
                                avatar={<FileOutlined />}
                                title={file.originalName}
                                description={
                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                        {formatSize(file.size)} ·{' '}
                                        {dayjs(file.createdAt).format('DD.MM.YYYY HH:mm')}
                                        {file.uploadedBy
                                            ? ` · ${file.uploadedBy.firstName} ${file.uploadedBy.lastName}`
                                            : ''}
                                    </Text>
                                }
                            />
                        </List.Item>
                    )}
                />
            )}
        </Space>
    );
}
